<template>
  <div class="plano-contas-form">
    <!-- General Information -->
    <box
      type="primary"
      :title="t('Informações gerais')"
      elem-id="boxInfoPlanoContas"
    >
      <div class="form-group">
        <franchise-select
          :label="t('Franquia')"
          :placeholder="t('Escolha a franquia')"
          v-model="selectedFranchiseId"
          :disabled="isViewMode"
          elem-id="planoContas_franquia_id"
          data-testid="plano-contas-franquia-select"
        />
      </div>
      <div class="form-group">
        <input-text
          :label="t('Nome do plano')"
          :placeholder="t('Ex: Plano 2026')"
          v-model="newPlanName"
          :disabled="isViewMode"
          elem-id="planoContas_nome"
          data-testid="plano-contas-nome-input"
        />
      </div>
      <Message v-if="!isViewMode" severity="info" class="clone-info-message">
        <span>{{ t('O plano clonado será criado como ativo, substituindo o plano ativo atual desta franquia.') }}</span>
      </Message>
    </box>

    <!-- Category Configuration -->
    <box
      type="primary"
      :title="t('Configuração das categorias')"
      elem-id="boxCategoriasPlanoContas"
    >
      <div v-if="loadingCategorias" class="loading-center">
        <ProgressSpinner />
        <p class="loading-text">{{ t('Carregando categorias...') }}</p>
      </div>

      <div v-else-if="categoryItems.length === 0" class="empty-state">
        <i class="pi pi-exclamation-triangle" style="font-size: 2rem; color: #f59e0b;"></i>
        <p class="empty-state-text">{{ t('Nenhuma categoria disponível') }}</p>
      </div>

      <div v-else class="category-config-table">
        <Message severity="info" class="category-info-message">
          <i class="pi pi-dollar"></i> <strong>{{ t('Fixa') }}</strong>: {{ t('define um valor fixo para a categoria') }}.
          &nbsp;·&nbsp;
          <i class="pi pi-percentage"></i> <strong>{{ t('Variável') }}</strong>: {{ t('define um percentual calculado sobre o faturamento do período') }}.
        </Message>

        <div class="category-list">
          <!-- Header -->
          <div class="category-row category-header">
            <div class="category-col-name">{{ t('Categoria') }}</div>
            <div class="category-col-tipo">{{ t('Tipo') }}</div>
            <div class="category-col-meta">{{ t('Meta') }}</div>
          </div>
          <!-- Rows -->
          <div
            v-for="(item, index) in categoryItems"
            :key="item.categoria_id"
            class="category-row"
          >
            <div class="category-col-name">
              <div :data-testid="`plano-contas-categoria-nome-${item.categoria_id}`">{{ item.titulo }}</div>
              <small
                v-if="item.descricao"
                class="category-descricao"
                :data-testid="`plano-contas-categoria-descricao-${item.categoria_id}`"
              >{{ item.descricao }}</small>
            </div>
            <div class="category-col-tipo">
              <SelectButton
                v-model="item.tipo"
                :options="tipoOptions"
                optionLabel="label"
                optionValue="value"
                :allowEmpty="false"
                :disabled="isFieldsDisabled"
                class="tipo-toggle"
                :data-testid="`plano-contas-tipo-toggle-${item.categoria_id}`"
                @change="onTipoChange(index)"
              />
            </div>
            <div class="category-col-meta">
              <input-currency
                v-if="item.tipo === 'fixa'"
                v-model="item.valor_previsto"
                :elem-id="`planoContas_valor_previsto-${item.categoria_id}`"
                :placeholder="t('Valor')"
                :disabled="isFieldsDisabled"
                :data-testid="`plano-contas-valor-fixo-${item.categoria_id}`"
              />
              <input-number
                v-else
                v-model="item.percentual_custom"
                :elem-id="`planoContas_percentual-${item.categoria_id}`"
                :placeholder="t('Percentual')"
                suffix="%"
                :min="0"
                :max="100"
                :minFractionDigits="1"
                :maxFractionDigits="2"
                :disabled="isFieldsDisabled"
                :data-testid="`plano-contas-percentual-${item.categoria_id}`"
              />
            </div>
          </div>
        </div>

        <!-- Summary -->
        <div class="category-summary">
          <span>
            {{ t('Total') }}: <strong>{{ categoryItems.length }}</strong> {{ t('categorias') }}
          </span>
          <Tag :value="`${contasFixasConfigCount} ${t('fixas')}`" severity="info" />
          <Tag :value="`${contasVariaveisConfigCount} ${t('variáveis')}`" severity="warning" />
        </div>
      </div>
    </box>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import SelectButton from '../../ui/SelectButton.vue';
import { usePlanoContas } from '../../composables/usePlanoContas';
import { useDespesasCategorias } from '../../composables/useDespesasCategorias';
import { useDespesas } from '../../composables/useDespesas';
import { eventBus } from '../../eventBus';
import permissionsService from '../../services/permissionsService';

type FranchiseId = number | string | null;
type CategoriaId = number | string;
type TipoCategoria = 'fixa' | 'variavel';

interface CategoryItem {
  categoria_id: CategoriaId;
  titulo: string;
  descricao: string | null;
  tipo: TipoCategoria;
  valor_previsto: number;
  percentual_custom: number | null;
}

interface Categoria {
  despesa_categoria_id: CategoriaId;
  despesa_categoria_titulo: string;
  despesa_categoria_percentual_padrao: number | string;
  [key: string]: unknown;
}

interface TemplateItem {
  categoria_id: CategoriaId;
  tipo?: TipoCategoria;
  valor_previsto?: number | string | null;
  percentual_custom?: number | null;
  [key: string]: unknown;
}

interface Template {
  itens?: TemplateItem[];
  [key: string]: unknown;
}

interface Plan {
  franquia_id?: FranchiseId;
  nome?: string;
  [key: string]: unknown;
}

type FormMode = 'create' | 'clone' | 'view';

const { t } = useI18n();
const { fetchTemplate, createTemplateVersion } = usePlanoContas();
const { fetchCategorias } = useDespesasCategorias();
const { fetchCategorias: fetchCategoriasTraduzidas } = useDespesas();

const props = withDefaults(defineProps<{
  mode?: FormMode;
  plan?: Plan | null;
}>(), {
  mode: 'create',
  plan: null
});

const emit = defineEmits<{
  saved: [];
}>();

const selectedFranchiseId = ref<FranchiseId>(null);
const newPlanName = ref<string>('');
const categoryItems = ref<CategoryItem[]>([]);
const loadingCategorias = ref<boolean>(false);
const isCloning = ref<boolean>(false);

const tipoOptions = computed<{ label: string; value: TipoCategoria }[]>(() => [
  { label: t('Fixa'), value: 'fixa' },
  { label: t('Variável'), value: 'variavel' }
]);

const isViewMode = computed<boolean>(() => props.mode === 'view');
const isCreateMode = computed<boolean>(() => props.mode === 'create');
const isFranqueado = computed<boolean>(() => permissionsService.isUserInGroups(['Franqueado']) && !permissionsService.isUserInGroups(['Full']));
const isFieldsDisabled = computed<boolean>(() => isViewMode.value || isFranqueado.value);

const contasFixasConfigCount = computed<number>(() => {
  return categoryItems.value.filter(i => i.tipo === 'fixa').length;
});

const contasVariaveisConfigCount = computed<number>(() => {
  return categoryItems.value.filter(i => i.tipo === 'variavel').length;
});

const canSave = computed<boolean>(() => {
  return !isCloning.value && !loadingCategorias.value && categoryItems.value.length > 0 && !!selectedFranchiseId.value;
});

const onTipoChange = (index: number): void => {
  const item = categoryItems.value[index];
  if (item.tipo === 'fixa') {
    item.percentual_custom = null;
    item.valor_previsto = 0;
  } else {
    item.valor_previsto = 0;
    item.percentual_custom = 0;
  }
};

const loadCategories = async (): Promise<void> => {
  loadingCategorias.value = true;
  try {
    const [categorias, traduzidas] = await Promise.all([
      fetchCategorias() as unknown as Promise<Categoria[]>,
      fetchCategoriasTraduzidas().catch(() => [])
    ]);
    const nomes = new Map(traduzidas.map(c => [String(c.Valor), c.Nome]));
    const tituloDe = (cat: Categoria): string =>
      nomes.get(String(cat.despesa_categoria_id)) ?? cat.despesa_categoria_titulo;
    // A descrição vem da busca traduzida (chamado 2026-0073); sem ela, nada.
    const descricoes = new Map(traduzidas.map(c => [String(c.Valor), typeof c.Descricao === 'string' ? c.Descricao : null]));
    const descricaoDe = (cat: Categoria): string | null =>
      descricoes.get(String(cat.despesa_categoria_id)) ?? null;

    if (props.mode === 'create') {
      categoryItems.value = categorias.map((cat): CategoryItem => {
        const hasDefaultPercentual = Number(cat.despesa_categoria_percentual_padrao) > 0;
        return {
          categoria_id: cat.despesa_categoria_id,
          titulo: tituloDe(cat),
          descricao: descricaoDe(cat),
          tipo: hasDefaultPercentual ? 'variavel' : 'fixa',
          valor_previsto: 0,
          percentual_custom: hasDefaultPercentual ? parseFloat(String(cat.despesa_categoria_percentual_padrao)) : null
        };
      });
    } else {
      const franquiaId = props.plan?.franquia_id as number | undefined;
      const template = (await fetchTemplate(franquiaId as number)) as unknown as Template | null;

      categoryItems.value = categorias.map((cat): CategoryItem => {
        const templateItem = template?.itens?.find(i => i.categoria_id === cat.despesa_categoria_id);
        const hasDefaultPercentual = Number(cat.despesa_categoria_percentual_padrao) > 0;
        return {
          categoria_id: cat.despesa_categoria_id,
          titulo: tituloDe(cat),
          descricao: descricaoDe(cat),
          tipo: templateItem?.tipo || (hasDefaultPercentual ? 'variavel' : 'fixa'),
          valor_previsto: Number(templateItem?.valor_previsto) || 0,
          percentual_custom: templateItem?.percentual_custom ?? (hasDefaultPercentual ? parseFloat(String(cat.despesa_categoria_percentual_padrao)) : null),
        };
      });
    }
  } catch (err) {
    console.error('Erro ao carregar categorias:', err);
  } finally {
    loadingCategorias.value = false;
  }
};

const save = async (): Promise<void> => {
  if (categoryItems.value.length === 0) return;

  isCloning.value = true;
  try {
    const itens = categoryItems.value.map(item => ({
      categoria_id: item.categoria_id,
      tipo: item.tipo,
      valor_previsto: item.tipo === 'fixa' ? (item.valor_previsto || 0) : 0,
      percentual_custom: item.tipo === 'variavel' ? item.percentual_custom : null
    }));

    const franquiaId = selectedFranchiseId.value;
    if (!franquiaId) {
      throw new Error(t('Selecione uma franquia'));
    }

    await createTemplateVersion(
      franquiaId as number,
      newPlanName.value || null,
      itens
    );

    const successMessage = isCreateMode.value
      ? t('Plano criado e ativado com sucesso')
      : t('Plano clonado e ativado com sucesso');

    eventBus.emit('globalMessage.show', {
      type: 'success',
      title: t('Sucesso'),
      message: successMessage
    });

    emit('saved');
    eventBus.emit('planocontas.cloned');
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.error('Erro ao salvar plano:', err);
    eventBus.emit('globalMessage.error', t('Erro ao salvar plano: ') + message);
    throw err;
  } finally {
    isCloning.value = false;
  }
};

const initForm = (): void => {
  if (props.mode === 'create') {
    selectedFranchiseId.value = null;
    newPlanName.value = '';
  } else if (props.plan) {
    selectedFranchiseId.value = props.plan.franquia_id ?? null;
    newPlanName.value = props.mode === 'clone'
      ? `${props.plan.nome || t('Plano')} (${t('clone')})`
      : props.plan.nome || '';
  }
  categoryItems.value = [];
  loadCategories();
};

watch(() => [props.mode, props.plan], () => {
  initForm();
}, { immediate: true });

defineExpose({
  save,
  canSave
});
</script>

<style scoped>
.form-group {
  margin-bottom: 15px;
}

.clone-info-message {
  margin-top: 12px;
}

.category-info-message {
  margin-bottom: 16px;
}

/* ========================================
   EMPTY / LOADING STATES
   ======================================== */
.empty-state {
  text-align: center;
  padding: 40px 0;
}

.empty-state-text {
  margin-top: 12px;
  color: #6b7280;
}

.loading-center {
  text-align: center;
  padding: 32px 0;
}

.loading-text {
  margin-top: 8px;
  color: #6b7280;
}

/* ========================================
   CATEGORY LIST (FLEX TABLE)
   ======================================== */
.category-list {
  max-height: 400px;
  overflow-y: auto;
}

.category-row {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #f3f4f6;
}

.category-row:last-child {
  border-bottom: none;
}

.category-header {
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #6b7280;
  font-weight: 600;
  background-color: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 1;
}

.category-col-name {
  flex: 1;
  font-weight: 600;
  min-width: 0;
}

.category-descricao {
  display: block;
  font-weight: 400;
  color: var(--p-text-muted-color);
}

.category-header .category-col-name {
  font-weight: 600;
}

.category-col-tipo {
  flex: 0 0 180px;
  display: flex;
  justify-content: center;
}

.category-col-meta {
  flex: 0 0 200px;
  display: flex;
  justify-content: center;
}

/* Remove bottom margin from inputs inside category rows */
.category-config-table :deep(.input-number-wrapper),
.category-config-table :deep(.form-group) {
  margin-bottom: 0;
}

/* Compact tipo toggle buttons */
.category-config-table :deep(.tipo-toggle.p-selectbutton) {
  display: inline-flex;
}

.category-config-table :deep(.tipo-toggle .p-togglebutton) {
  padding: 0.3rem 0.6rem;
  font-size: 0.8rem;
}

/* ========================================
   CATEGORY SUMMARY FOOTER
   ======================================== */
.category-summary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid #e5e7eb;
  color: #6b7280;
  font-size: 0.875rem;
}

/* ========================================
   RESPONSIVE
   ======================================== */
@media (max-width: 768px) {
  .category-col-tipo {
    flex: 0 0 140px;
  }

  .category-col-meta {
    flex: 0 0 150px;
  }
}
</style>
